import React from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
    CalendarIcon, LocationIcon, ArticleIcon,
    ArrowRightIcon, InfoIcon, CheckCircleIcon,
    AlertIcon, SparklesIcon, TeamIcon, MailIcon
} from './Icons';

/**
 * Parses markdown into safe HTML.
 */
export function formatBobMarkdown(text) {
    if (!text) return '';

    let formatted = typeof text === 'string' ? text : '';

    // If text is accidentally a JSON string, extract the text field
    if (formatted.trim().startsWith('{') && formatted.includes('"text"')) {
        try {
            const parsed = JSON.parse(formatted);
            if (parsed.text) formatted = parsed.text;
        } catch (_) {}
    }

    // Code blocks (```language ... ```)
    formatted = formatted.replace(/```(?:[a-zA-Z0-9_-]+)?\s*([\s\S]*?)\s*```/g, '<pre class="p-3 my-2 rounded-xl bg-surface-containerHighest text-surface-on font-mono text-xs overflow-x-auto border border-outlineVariant/30"><code>$1</code></pre>');

    // Inline code (`code`)
    formatted = formatted.replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 rounded bg-surface-containerHighest text-primary font-mono text-[11px]">$1</code>');

    // Headers (### Header, ## Header, # Header)
    formatted = formatted.replace(/^### (.*$)/gim, '<h4 class="font-bold text-xs uppercase tracking-wider text-primary mt-2.5 mb-1">$1</h4>');
    formatted = formatted.replace(/^## (.*$)/gim, '<h3 class="font-bold text-sm text-surface-on mt-2.5 mb-1">$1</h3>');
    formatted = formatted.replace(/^# (.*$)/gim, '<h2 class="font-bold text-base text-surface-on mt-3 mb-1.5">$1</h2>');

    // Bold (**text** or __text__)
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-surface-on">$1</strong>');
    formatted = formatted.replace(/__(.*?)__/g, '<strong class="font-bold text-surface-on">$1</strong>');

    // Italic (*text* or _text_)
    formatted = formatted.replace(/\*([^\*]+)\*/g, '<em class="italic">$1</em>');
    formatted = formatted.replace(/_([^_]+)_/g, '<em class="italic">$1</em>');

    // Markdown Links [Label](url)
    formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline font-semibold bob-chat-link" data-bob-url="$2">$1</a>');

    // Blockquotes (> text)
    formatted = formatted.replace(/^> (.*$)/gim, '<blockquote class="border-l-2 border-primary/60 pl-2.5 my-1 text-surface-onVariant/90 italic">$1</blockquote>');

    // Bullet lists (- item or * item)
    formatted = formatted.replace(/^\s*[-*]\s+(.*$)/gim, '<div class="flex items-start gap-1.5 my-0.5"><span class="text-primary font-black select-none">•</span><span>$1</span></div>');

    // Numbered lists (1. item)
    formatted = formatted.replace(/^\s*(\d+)\.\s+(.*$)/gim, '<div class="flex items-start gap-1.5 my-0.5"><span class="text-primary font-bold select-none text-[11px]">$1.</span><span>$2</span></div>');

    // Line breaks to <br/> (unless already inside tags)
    formatted = formatted.replace(/\n/g, '<br/>');

    return DOMPurify.sanitize(formatted, {
        ALLOWED_TAGS: ['strong', 'em', 'code', 'pre', 'h2', 'h3', 'h4', 'a', 'blockquote', 'div', 'span', 'br', 'p', 'b', 'i'],
        ALLOWED_ATTR: ['href', 'class', 'data-bob-url', 'target', 'rel']
    });
}

/**
 * Bob Markdown Viewer with internal link interceptor
 */
export const BobMarkdown = ({ content, onLinkClick }) => {
    const navigate = useNavigate();

    const handleClick = (e) => {
        const link = e.target.closest('a[data-bob-url]');
        if (link) {
            e.preventDefault();
            const url = link.getAttribute('data-bob-url');
            if (onLinkClick) {
                onLinkClick(url);
            } else if (url.startsWith('http')) {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                navigate(url);
            }
        }
    };

    return (
        <div 
            onClick={handleClick}
            className="text-body-medium text-surface-on leading-relaxed break-words"
            dangerouslySetInnerHTML={{ __html: formatBobMarkdown(content) }}
        />
    );
};

/**
 * Event Card
 */
export const BobEventCard = ({ event, onAction }) => {
    const navigate = useNavigate();

    let dayStr = '';
    let monthStr = '';
    if (event.date) {
        const d = new Date(event.date);
        if (!isNaN(d.getTime())) {
            dayStr = d.getDate();
            const months = ['JAN', 'FEB', 'MÄR', 'APR', 'MAI', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DEZ'];
            monthStr = months[d.getMonth()];
        }
    }

    const targetUrl = event.url || (event.slug ? `/events/${event.slug}` : (event.id ? `/events/${event.id}` : null));

    const handleNavigate = () => {
        if (onAction) {
            onAction({ type: 'navigate', url: targetUrl });
        } else if (targetUrl) {
            navigate(targetUrl);
        }
    };

    return (
        <div className="w-full bg-surface-containerHigh border border-outlineVariant/30 hover:border-primary/40 rounded-2xl p-3.5 shadow-sm hover:shadow-md transition-all flex flex-col gap-2 relative overflow-hidden group">
            <div className="flex items-start gap-3">
                {/* Date Badge */}
                {dayStr && (
                    <div className="flex flex-col items-center justify-center w-12 h-12 rounded-xl bg-primary text-primary-on flex-shrink-0 shadow-xs">
                        <span className="text-[10px] font-black tracking-wider leading-none uppercase">{monthStr}</span>
                        <span className="text-base font-black leading-none mt-0.5">{dayStr}</span>
                    </div>
                )}

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {event.category && (
                            <span className="text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                                {event.category}
                            </span>
                        )}
                        {event.time && (
                            <span className="text-[11px] text-surface-onVariant flex items-center gap-1">
                                🕒 {event.time}
                            </span>
                        )}
                    </div>
                    <h4 className="text-sm font-bold text-surface-on mt-1 leading-snug truncate group-hover:text-primary transition-colors">
                        {event.title}
                    </h4>
                </div>
            </div>

            {event.location && (
                <div className="flex items-center gap-1.5 text-xs text-surface-onVariant/90">
                    <LocationIcon style={{ fontSize: 13 }} className="text-primary flex-shrink-0" />
                    <span className="truncate">{event.location}</span>
                </div>
            )}

            {event.description && (
                <p className="text-xs text-surface-onVariant line-clamp-2 leading-relaxed">
                    {event.description}
                </p>
            )}

            {targetUrl && (
                <button
                    type="button"
                    onClick={handleNavigate}
                    className="mt-1 w-full py-1.5 px-3 rounded-xl bg-primary/10 hover:bg-primary text-primary hover:text-primary-on text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-xs"
                >
                    <span>Details ansehen</span>
                    <ArrowRightIcon style={{ fontSize: 14 }} />
                </button>
            )}
        </div>
    );
};

/**
 * Blog Card
 */
export const BobBlogCard = ({ blog, onAction }) => {
    const navigate = useNavigate();
    const targetUrl = blog.url || (blog.id ? `/blog/${blog.id}` : null);

    const handleNavigate = () => {
        if (onAction) {
            onAction({ type: 'navigate', url: targetUrl });
        } else if (targetUrl) {
            navigate(targetUrl);
        }
    };

    return (
        <div className="w-full bg-surface-containerHigh border border-outlineVariant/30 hover:border-secondary/50 rounded-2xl p-3.5 shadow-sm hover:shadow-md transition-all flex flex-col gap-2 relative group">
            <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-secondary/15 text-secondary flex items-center justify-center flex-shrink-0">
                    <ArticleIcon style={{ fontSize: 18 }} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-[9px] font-extrabold uppercase tracking-wider text-secondary">
                            Blogbeitrag
                        </span>
                        {blog.date && (
                            <span className="text-[10px] text-surface-onVariant/70">
                                • {blog.date}
                            </span>
                        )}
                    </div>
                    <h4 className="text-sm font-bold text-surface-on mt-0.5 leading-snug group-hover:text-secondary transition-colors">
                        {blog.title}
                    </h4>
                </div>
            </div>

            {blog.excerpt && (
                <p className="text-xs text-surface-onVariant line-clamp-2 leading-relaxed">
                    {blog.excerpt}
                </p>
            )}

            {targetUrl && (
                <button
                    type="button"
                    onClick={handleNavigate}
                    className="mt-1 w-full py-1.5 px-3 rounded-xl bg-secondary/10 hover:bg-secondary text-secondary hover:text-secondary-on text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-xs"
                >
                    <span>Beitrag ansehen</span>
                    <ArrowRightIcon style={{ fontSize: 14 }} />
                </button>
            )}
        </div>
    );
};

/**
 * Group / Stufen Card
 */
export const BobGroupCard = ({ group, onAction }) => {
    return (
        <div className="w-full bg-surface-containerHigh border border-outlineVariant/30 rounded-2xl p-3.5 shadow-sm flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        <TeamIcon style={{ fontSize: 18 }} />
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-surface-on">{group.name}</h4>
                        {group.altersstufe && (
                            <span className="text-[10px] font-semibold text-primary">{group.altersstufe}</span>
                        )}
                    </div>
                </div>
            </div>

            {group.meetingTime && (
                <div className="text-xs text-surface-onVariant/90 bg-surface/40 p-2 rounded-xl border border-outlineVariant/20 flex items-center gap-1.5">
                    <CalendarIcon style={{ fontSize: 13 }} className="text-primary flex-shrink-0" />
                    <span>{group.meetingTime}</span>
                </div>
            )}

            {group.description && (
                <p className="text-xs text-surface-onVariant leading-relaxed">
                    {group.description}
                </p>
            )}
        </div>
    );
};

/**
 * Callout / Info Box
 */
export const BobCallout = ({ callout }) => {
    const variantStyles = {
        info: 'bg-blue-500/10 border-blue-500/30 text-blue-800 dark:text-blue-200',
        success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-800 dark:text-emerald-200',
        warning: 'bg-amber-500/10 border-amber-500/30 text-amber-800 dark:text-amber-200',
        tip: 'bg-purple-500/10 border-purple-500/30 text-purple-800 dark:text-purple-200'
    };

    const variantIcons = {
        info: <InfoIcon style={{ fontSize: 16 }} className="text-blue-600 dark:text-blue-400" />,
        success: <CheckCircleIcon style={{ fontSize: 16 }} className="text-emerald-600 dark:text-emerald-400" />,
        warning: <AlertIcon style={{ fontSize: 16 }} className="text-amber-600 dark:text-amber-400" />,
        tip: <SparklesIcon style={{ fontSize: 16 }} className="text-purple-600 dark:text-purple-400" />
    };

    const style = variantStyles[callout.variant] || variantStyles.info;
    const icon = variantIcons[callout.variant] || variantIcons.info;

    return (
        <div className={`w-full rounded-2xl border p-3 flex gap-2.5 items-start text-xs shadow-xs ${style}`}>
            <div className="mt-0.5 flex-shrink-0">{icon}</div>
            <div className="flex-1 min-w-0">
                {callout.title && <div className="font-bold text-xs mb-0.5">{callout.title}</div>}
                <div className="leading-relaxed opacity-90">{callout.text || callout.content}</div>
            </div>
        </div>
    );
};

/**
 * Quick Reply Chips
 */
export const BobQuickReplies = ({ options, onSelect }) => {
    if (!options || !Array.isArray(options) || options.length === 0) return null;

    return (
        <div className="flex flex-wrap gap-1.5 mt-1 pt-1 w-full animate-fade-in">
            {options.map((opt, idx) => (
                <button
                    key={idx}
                    type="button"
                    onClick={() => onSelect(opt)}
                    className="text-xs font-semibold bg-surface-containerHighest/80 hover:bg-primary hover:text-white text-surface-on border border-outlineVariant/30 px-3 py-1.5 rounded-full transition-all shadow-xs hover:shadow-sm hover:scale-[1.02] active:scale-[0.98] text-left flex items-center gap-1"
                >
                    <span>{opt}</span>
                </button>
            ))}
        </div>
    );
};

/**
 * Action Button
 */
export const BobActionButton = ({ action, onAction }) => {
    const navigate = useNavigate();

    const handleClick = () => {
        if (action.target || action.url) {
            const url = action.target || action.url;
            if (url.startsWith('http')) {
                window.open(url, '_blank');
            } else {
                navigate(url);
            }
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            className="w-full py-2 px-4 rounded-xl bg-primary text-white hover:bg-primary/95 text-xs font-bold transition-all flex items-center justify-center gap-2 shadow-sm hover:shadow"
        >
            <span>{action.label || 'Aktion öffnen'}</span>
            <ArrowRightIcon style={{ fontSize: 14 }} />
        </button>
    );
};

/**
 * Google Search Grounding Sources Display
 */
export const BobGroundingSources = ({ grounding }) => {
    if (!grounding || (!grounding.sources?.length && !grounding.queries?.length)) return null;

    return (
        <div className="w-full max-w-[85%] mt-1.5 p-3 rounded-2xl bg-surface-containerHigh/60 border border-outlineVariant/30 text-xs shadow-xs animate-fade-in flex flex-col gap-2">
            {grounding.queries?.length > 0 && (
                <div className="flex items-center gap-2 text-surface-onVariant flex-wrap">
                    <span className="font-bold text-primary flex items-center gap-1">
                        🔍 Gegoogelt nach:
                    </span>
                    <div className="flex flex-wrap gap-1">
                        {grounding.queries.map((q, idx) => (
                            <span key={idx} className="px-2 py-0.5 rounded-full bg-surface-containerHighest text-surface-on text-[11px] font-medium border border-outlineVariant/20">
                                "{q}"
                            </span>
                        ))}
                    </div>
                </div>
            )}
            {grounding.sources?.length > 0 && (
                <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-surface-onVariant/70">
                        Quellen aus dem Web:
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                        {grounding.sources.map((s, idx) => {
                            let domain = '';
                            try {
                                domain = new URL(s.url).hostname.replace(/^www\./, '');
                            } catch (_) {
                                domain = s.url;
                            }
                            return (
                                <a
                                    key={idx}
                                    href={s.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-surface-containerHigh hover:bg-primary hover:text-white text-surface-on text-[11px] font-medium transition-all border border-outlineVariant/30 shadow-xs hover:shadow group"
                                    title={s.title || s.url}
                                >
                                    <span className="text-primary group-hover:text-white text-[12px]">🌐</span>
                                    <span className="truncate max-w-[180px]">{s.title || domain}</span>
                                </a>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Main BobRichElements Wrapper
 */
export const BobRichElements = ({ ui, onQuickReply, onAction }) => {
    if (!ui || !Array.isArray(ui) || ui.length === 0) return null;

    return (
        <div className="w-full flex flex-col gap-2.5 max-w-[92%] mt-1 pl-1 text-surface-on animate-fade-in">
            {ui.map((item, idx) => {
                if (!item || !item.type) return null;

                switch (item.type) {
                    case 'event_card':
                    case 'event':
                        return <BobEventCard key={idx} event={item} onAction={onAction} />;

                    case 'blog_card':
                    case 'blog':
                        return <BobBlogCard key={idx} blog={item} onAction={onAction} />;

                    case 'group_card':
                    case 'group':
                        return <BobGroupCard key={idx} group={item} onAction={onAction} />;

                    case 'callout':
                    case 'info_box':
                        return <BobCallout key={idx} callout={item} />;

                    case 'quick_replies':
                    case 'suggestions':
                        return <BobQuickReplies key={idx} options={item.options} onSelect={onQuickReply} />;

                    case 'action_button':
                        return <BobActionButton key={idx} action={item} onAction={onAction} />;

                    case 'grounding':
                    case 'sources':
                        return <BobGroundingSources key={idx} grounding={item} />;

                    default:
                        return null;
                }
            })}
        </div>
    );
};

export default BobRichElements;

